'use client';
import { useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import { isInApp } from '@/libs/comeAppSDK';

interface FocusFixComProps {
  /**
   * 输入框标志属性名，默认 'data-keyboard-fix'
   * 在需要检测的输入框上添加此属性即可
   * 例如: <input data-keyboard-fix />
   */
  flagAttribute?: string;
  /**
   * 默认键盘高度，当无法检测到实际键盘高度时使用
   * 默认 300px
   */
  keyboardHeight?: number;
  /**
   * 聚焦时的回调
   */
  onInputFocus?: (element: HTMLInputElement | HTMLTextAreaElement) => void;
  /**
   * 失焦时的回调
   */
  onInputBlur?: (element: HTMLInputElement | HTMLTextAreaElement) => void;
  /**
   * 自定义 className
   */
  className?: string;
  /**
   * 自定义样式
   */
  style?: React.CSSProperties;
}

export default function FocusFixCom({
  flagAttribute = 'data-keyboard-fix',
  keyboardHeight = 300,
  onInputFocus,
  onInputBlur,
  className = '',
  style,
}: FocusFixComProps) {
  const [height, setHeight] = useState(0);
  const blurTimerRef = useRef<NodeJS.Timeout | null>(null);
  const focusTimerRef = useRef<NodeJS.Timeout | null>(null);
  // 记录初始视口高度，用于计算键盘高度
  const initialViewportHeightRef = useRef<number>(0);

  useEffect(() => {
    // 只在 webview 环境 中执行
    if (!isInApp()) {
      return;
    }

    // 记录初始视口高度（页面加载时，键盘未弹出）
    const vv = window.visualViewport;
    if (vv) {
      initialViewportHeightRef.current = vv.height;
    } else {
      // 如果 visualViewport 不可用，使用 window.innerHeight
      initialViewportHeightRef.current = window.innerHeight;
    }
    console.log('[FocusFixCom] Initial viewport height:', initialViewportHeightRef.current);

    // 检查元素是否有标志属性
    const hasFlag = (el: HTMLElement): boolean => {
      return el.hasAttribute(flagAttribute);
    };

    // 计算实际键盘高度 - 使用多种方法组合提高兼容性
    const calculateKeyboardHeight = (): number => {
      const vv = window.visualViewport;

      // 方法1: 使用 visualViewport (iOS Safari / Chrome Android 支持较好)
      if (vv && initialViewportHeightRef.current > 0) {
        const heightDiff = initialViewportHeightRef.current - vv.height;
        console.log('[FocusFixCom] Method 1 - visualViewport:', {
          initial: initialViewportHeightRef.current,
          current: vv.height,
          diff: heightDiff,
        });

        if (heightDiff > 50) {
          return heightDiff;
        }
      }

      // 方法2: 对比 window.innerHeight 与 visualViewport.height
      if (vv) {
        const heightDiff = window.innerHeight - vv.height;
        console.log('[FocusFixCom] Method 2 - innerHeight vs viewport:', {
          innerHeight: window.innerHeight,
          viewportHeight: vv.height,
          diff: heightDiff,
        });

        if (heightDiff > 50) {
          return heightDiff;
        }
      }

      // 方法3: 对比初始 window.innerHeight 与当前值
      if (initialViewportHeightRef.current > 0) {
        const currentInnerHeight = window.innerHeight;
        const heightDiff = initialViewportHeightRef.current - currentInnerHeight;
        console.log('[FocusFixCom] Method 3 - initial vs current innerHeight:', {
          initial: initialViewportHeightRef.current,
          current: currentInnerHeight,
          diff: heightDiff,
        });

        if (heightDiff > 50) {
          return heightDiff;
        }
      }

      // 方法4: 使用默认键盘高度作为 fallback
      console.log('[FocusFixCom] Using default keyboard height:', keyboardHeight);
      return keyboardHeight;
    };

    // 输入框聚焦事件
    const handleFocusIn = (e: Event) => {
      const el = e.target as HTMLInputElement | HTMLTextAreaElement;
      if (!el || (el.tagName !== 'INPUT' && el.tagName !== 'TEXTAREA')) return;
      if (el.readOnly || el.disabled) return;

      // 检查是否有标志属性
      if (!hasFlag(el)) return;

      // 清除之前的定时器
      if (blurTimerRef.current) {
        clearTimeout(blurTimerRef.current);
        blurTimerRef.current = null;
      }
      if (focusTimerRef.current) {
        clearTimeout(focusTimerRef.current);
        focusTimerRef.current = null;
      }

      console.log('[FocusFixCom] Input focused:', el.tagName, el.type);
      onInputFocus?.(el);

      // 延迟设置高度，等待键盘动画
      focusTimerRef.current = setTimeout(() => {
        const actualKeyboardHeight = calculateKeyboardHeight();
        setHeight(actualKeyboardHeight);
        console.log('[FocusFixCom] Set fixCom height:', actualKeyboardHeight, 'px');
      }, 300); // 等待键盘动画完成
    };

    // 输入框失焦事件
    const handleFocusOut = (e: Event) => {
      const el = e.target as HTMLInputElement | HTMLTextAreaElement;
      if (!el || (el.tagName !== 'INPUT' && el.tagName !== 'TEXTAREA')) return;

      // 检查是否有标志属性
      if (!hasFlag(el)) return;

      console.log('[FocusFixCom] Input blurred:', el.tagName, el.type);
      onInputBlur?.(el);

      // 延迟还原高度，防止清除按钮等操作导致误判
      blurTimerRef.current = setTimeout(() => {
        setHeight(0);
        console.log('[FocusFixCom] Reset fixCom height: 0px');
      }, 200);
    };

    // 注册事件监听器
    document.addEventListener('focusin', handleFocusIn);
    document.addEventListener('focusout', handleFocusOut);

    // 监听 visualViewport 的 resize 事件，检测键盘收起
    const handleViewportResize = () => {
      const vv = window.visualViewport;
      if (vv) {
        const heightDiff = window.innerHeight - vv.height;

        // 如果高度差小于50px，说明键盘已经收起
        if (heightDiff < 50) {
          // 清除之前的定时器
          if (blurTimerRef.current) {
            clearTimeout(blurTimerRef.current);
            blurTimerRef.current = null;
          }
          if (focusTimerRef.current) {
            clearTimeout(focusTimerRef.current);
            focusTimerRef.current = null;
          }

          // 立即还原高度
          setHeight(0);
        }
      }
    };

    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', handleViewportResize);
    }

    return () => {
      // 清理事件监听器
      document.removeEventListener('focusin', handleFocusIn);
      document.removeEventListener('focusout', handleFocusOut);

      if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', handleViewportResize);
      }

      // 清理定时器
      if (blurTimerRef.current) {
        clearTimeout(blurTimerRef.current);
      }
      if (focusTimerRef.current) {
        clearTimeout(focusTimerRef.current);
      }
    };
  }, [flagAttribute, keyboardHeight, onInputFocus, onInputBlur]);

  return (
    <div
      className={clsx('w-full', className)}
      style={{
        height: `${height + 10}px`,
        transition: 'height 0.15s ease-in-out',
        ...style,
      }}
    />
  );
}